import React from 'react';
import ReactDOM from 'react-dom';
import { TrelloStorage } from '@trello/storage';

export { findDOMNode, createPortal } from 'react-dom';

type RenderingMode = 'strict' | 'concurrent' | null;

const renderingMode: RenderingMode = TrelloStorage.get('react.rendering-mode');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const concurrentRootMap = new WeakMap<Element | DocumentFragment, any>();

/**
 * Use localStorage switch to flip which React rendering
 * mode to use.
 */
export const render = (
  Component: React.ReactElement,
  element: Element | DocumentFragment,
  callback?: () => void | undefined,
) => {
  let root;
  switch (renderingMode) {
    case 'concurrent':
      /**
       * Render the component tree using React's unstable
       * ConcurrentMode.
       */
      // @ts-ignore
      root = ReactDOM.unstable_createRoot(element);
      concurrentRootMap.set(element, root);
      return root.render(Component, callback);
    case 'strict':
      /**
       * Render the component tree wrapped in <StrictMode> so that
       * React prints any usages of deprecated APIs to the console
       */
      return ReactDOM.render(
        <React.StrictMode>{Component}</React.StrictMode>,
        element,
        callback,
      );
    default:
      /**
       * Render in regular mode
       */
      return ReactDOM.render(Component, element, callback);
  }
};

export const unmountComponentAtNode = (
  element: Element | DocumentFragment,
): boolean => {
  if (renderingMode === 'concurrent') {
    const root = concurrentRootMap.get(element);
    if (root) {
      concurrentRootMap.delete(element);
      root.unmount();
      return true;
    }
    return false;
  }

  return ReactDOM.unmountComponentAtNode(element);
};
